import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Loading } from 'react-admin';
import { RequireAccess } from '../auth/RequireAccess';
import { SubscriptionsBand } from './SubscriptionsBand';
import { useSubscriptions } from './useSubscriptions';

/**
 * Plus subscriptions - the first Phase 4 surface and the first MUTATING analytical
 * surface (Reports-adjacent admin view at /subscriptions). Unlike the read-only
 * reports suite, this surface writes, so the audit floor is LIVE here: both row
 * actions ride the performStatusTransition + withAudit chokepoint.
 *
 * It is a FILTERED VIEW over users (members with plusStatus != none), banded per
 * market the scope derives to (UK one band, US one band, GLOBAL two stacked),
 * keyed off the region scope, not the switcher. Plus is a wallet-funded prepaid
 * subscription (D-subscriptions): there is no Stripe and no card billing anywhere.
 *
 * Each band leads on honest MRR (active subscriptions x the period price, single
 * currency, no cross-market total) with the at-risk (pending_cancel) figure
 * beside it, then the four-state breakdown, then the member list with the two
 * guarded actions where the role's capability permits.
 */

/** The surface without the access wrapper. Exported so tests can inject `asOf`. */
export const SubscriptionsView = ({ asOf }: { asOf?: number }) => {
  // One reference clock, captured once via a lazy initializer (so the render body
  // stays pure). Used by the comp action to date the granted period. Tests inject
  // a fixed value for determinism.
  const [now] = useState<number>(() => asOf ?? Date.now());

  const { view, isLoading } = useSubscriptions();

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: 1 }}
      >
        Members
      </Typography>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
        Plus subscriptions
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Plus is a prepaid period funded from the member wallet, auto-renewed by
        debiting the wallet again. MRR is active subscriptions times the period
        price, per market, single currency.
      </Typography>

      {isLoading || !view ? (
        <Loading />
      ) : view.bands.length === 0 ? (
        <Typography color="text.secondary">
          No market is in scope for this account.
        </Typography>
      ) : (
        view.bands.map((band) => (
          <SubscriptionsBand key={band.market} band={band} now={now} />
        ))
      )}
    </Box>
  );
};

/**
 * Gated entry registered at /subscriptions. `subscriptions:read` is a read
 * capability, so viewer (read everywhere) and super_admin see it, and editor is
 * granted it explicitly (D-subscriptions); the placeholder support / fraud roles
 * do not. The two write actions gate themselves on finer action capabilities.
 */
export const Subscriptions = () => (
  <RequireAccess capability="subscriptions:read">
    <SubscriptionsView />
  </RequireAccess>
);
